import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useBusiness } from '../../../contexts/BusinessContext';
import {
  createLeadForm,
  fetchLeadForms,
  type LeadForm,
} from '../../../lib/crm/leadForms';
import CreateLeadFormModal from '../../../components/crm/lead-forms/CreateLeadFormModal';

const shell = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
};

export default function LeadFormsLibrary() {
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const [forms, setForms] = useState<LeadForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const data = await fetchLeadForms(businessId);
      setForms(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load lead forms');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (title: string) => {
    if (!businessId) return;
    setCreating(true);
    try {
      const form = await createLeadForm(businessId, title);
      setCreateOpen(false);
      toast.success('Lead form created');
      navigate(`/crm/lead-forms/${form.id}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create lead form');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="flex flex-col bg-[#f1f2f4] overflow-hidden"
      style={{
        ...shell,
        height: 'calc(100dvh - var(--control-centre-activity-rail-top, 3rem))',
      }}
      data-crm-lead-forms
    >
      <div className="shrink-0 bg-white border-b border-gray-200/60 px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12px] font-semibold uppercase tracking-[0.04em] text-gray-400 mb-0.5">
            CRM
          </p>
          <h1
            className="text-xl font-bold text-gray-900"
            style={{ letterSpacing: '-0.02em' }}
          >
            Lead Forms
          </h1>
          <p className="text-[13px] text-gray-500 mt-0.5">
            Build shareable forms that capture leads
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-blue-600 text-white rounded-[10px] text-[14px] font-semibold shadow-sm touch-manipulation self-start sm:self-auto"
          style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
        >
          <Plus className="h-4 w-4" />
          Create form
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="h-full flex items-center justify-center text-[14px] text-gray-500">
            Loading…
          </div>
        ) : forms.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center px-6 text-center">
            <div className="w-14 h-14 mb-4 rounded-[12px] bg-white border border-gray-200/60 flex items-center justify-center shadow-sm">
              <FileText className="h-7 w-7 text-gray-400" />
            </div>
            <h2 className="text-[17px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
              No lead forms yet
            </h2>
            <p className="text-[14px] text-gray-500 mt-1 mb-5 max-w-sm">
              Create your first form to start capturing leads from your website or links.
            </p>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-blue-600 text-white rounded-[10px] text-[14px] font-semibold touch-manipulation"
              style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
            >
              <Plus className="h-4 w-4" />
              Create form
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200/60 bg-white min-h-full">
            {forms.map((form) => {
              const fieldCount = form.form_config?.fields?.length ?? 0;
              return (
                <button
                  key={form.id}
                  type="button"
                  onClick={() => navigate(`/crm/lead-forms/${form.id}`)}
                  className="w-full text-left px-4 sm:px-6 py-4 hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-[10px] bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-gray-700" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p
                          className="text-[15px] font-semibold text-gray-900 truncate"
                          style={{ letterSpacing: '-0.01em' }}
                        >
                          {form.title}
                        </p>
                        <span
                          className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${
                            form.is_active
                              ? 'bg-green-50 text-green-700 border border-green-200'
                              : 'bg-gray-100 text-gray-500 border border-gray-200'
                          }`}
                        >
                          {form.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-[13px] text-gray-500 mt-0.5">
                        {fieldCount === 0
                          ? 'No fields yet'
                          : `${fieldCount} field${fieldCount === 1 ? '' : 's'}`}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <CreateLeadFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        loading={creating}
      />
    </div>
  );
}
